import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Download, Copy, RefreshCw, ExternalLink, MessageSquare, X, Edit3, Save,
  AlertTriangle, EyeOff,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MainOfferViolations — Produse Care nu au Oferta Principala.
 *
 * A background job scans every hour every ACTIVE eMAG offer.  Whenever a
 * SKU's Buy-Button rank hits 2 we surface it here so the operator can
 * dispatch a trademark-infringement complaint against the seller that
 * stole the Buy Button.
 *
 * Docs are generated per-platform on-demand: PNKs on the same integration
 * key are grouped into one Sesizare + one Mesaj EMAG so we minimise the
 * amount of paperwork.
 */
export default function MainOfferViolations() {
  const [loading, setLoading] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [byPlatform, setByPlatform] = useState({});
  const [popup, setPopup] = useState(null); // {integration_key, platform_label, title, body}
  const [editRow, setEditRow] = useState(null); // row being edited

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/main-offer-violations`);
      setRows(r.data?.items || []);
      setByPlatform(r.data?.by_platform || {});
    } catch (e) {
      toast.error("Nu pot încărca lista", { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const scanNow = async () => {
    setScanBusy(true);
    try {
      const r = await axios.post(`${API}/main-offer-violations/scan-now`);
      toast.success("Scanare completă", { description: `Găsit: ${r.data.found} · Nou: ${r.data.new} · Șters: ${r.data.removed}` });
      load();
    } catch (e) {
      toast.error("Eșuat", { description: e.response?.data?.detail || e.message });
    } finally { setScanBusy(false); }
  };

  const downloadDoc = (kind, integrationKey, platformLabel) => {
    const url = `${API}/main-offer-violations/download/${kind}?integration_key=${encodeURIComponent(integrationKey)}`;
    // Use anchor so browser triggers a native download (preserves the
    // Content-Disposition filename from backend).
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.info(`Descărcare ${kind === "sesizare" ? "Sesizare" : "Mesaj eMAG"} pentru ${platformLabel}`);
  };

  const openMesajPopup = async (integrationKey) => {
    try {
      const r = await axios.get(`${API}/main-offer-violations/mesaj-text?integration_key=${encodeURIComponent(integrationKey)}`);
      setPopup(r.data);
    } catch (e) {
      toast.error("Nu pot deschide mesajul", { description: e.response?.data?.detail || e.message });
    }
  };

  const saveSeller = async (row, newSeller) => {
    try {
      await axios.patch(
        `${API}/main-offer-violations/${encodeURIComponent(row.integration_key)}/${encodeURIComponent(row.pnk)}/seller`,
        { seller: newSeller },
      );
      setRows((prev) => prev.map((r) =>
        r.integration_key === row.integration_key && r.pnk === row.pnk ? { ...r, seller: newSeller } : r,
      ));
      setEditRow(null);
      toast.success("Seller actualizat");
    } catch (e) {
      toast.error("Eșuat", { description: e.response?.data?.detail || e.message });
    }
  };

  const ignoreProduct = async (row) => {
    // Confirmare — acțiunea e reversibilă doar prin call manual la
    // DELETE /ignored/{integration_key}/{pnk}, deci merită întrebat.
    const ok = window.confirm(
      `Ignoră produsul „${row.sku || row.pnk}" pe ${row.platform_label}?\n\n` +
      `Nu va mai apărea niciodată în această listă pentru platforma respectivă. ` +
      `Poți anula manual dacă te răzgândești.`,
    );
    if (!ok) return;
    try {
      await axios.post(
        `${API}/main-offer-violations/${encodeURIComponent(row.integration_key)}/${encodeURIComponent(row.pnk)}/ignore`,
      );
      // Elimină rândul local + reactualizează contorul per platformă.
      setRows((prev) => prev.filter((r) => !(r.integration_key === row.integration_key && r.pnk === row.pnk)));
      setByPlatform((prev) => {
        const next = { ...prev };
        const remaining = (next[row.integration_key] || 1) - 1;
        if (remaining <= 0) delete next[row.integration_key];
        else next[row.integration_key] = remaining;
        return next;
      });
      toast.success(`Produs ignorat pe ${row.platform_label}`);
    } catch (e) {
      toast.error("Eșuat", { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-4 p-4" data-testid="main-offer-violations-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-zinc-100">
            <AlertTriangle className="h-6 w-6 text-orange-300" />
            Produse Care nu au Oferta Principala
          </h1>
          <p className="mt-0.5 text-sm text-zinc-500">
            SKU-uri active cu Buy Button rank = 2 — un alt seller are oferta principală.
            Scanare automată la fiecare oră; e-mail trimis pentru violări noi.
          </p>
        </div>
        <button
          type="button"
          onClick={scanNow}
          disabled={scanBusy || loading}
          data-testid="scan-now-btn"
          className="inline-flex items-center gap-1.5 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 text-sm text-orange-100 hover:bg-orange-500/20 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${scanBusy ? "animate-spin" : ""}`} />
          {scanBusy ? "Scanez…" : "Scanează acum"}
        </button>
      </div>

      {/* Per-platform download strip */}
      {Object.keys(byPlatform).length > 0 && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3" data-testid="platform-actions">
          <div className="mb-2 text-[11px] uppercase tracking-wider text-zinc-500">
            Descarcă documentele grupate pe platformă
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(byPlatform).map(([key, count]) => {
              const label = rows.find((r) => r.integration_key === key)?.platform_label || key;
              return (
                <div key={key} className="flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1">
                  <span className="text-xs font-medium text-zinc-200">{label}</span>
                  <span className="rounded-full bg-orange-500/20 px-1.5 py-0 text-[10px] text-orange-100">{count} PNK</span>
                  <button
                    type="button"
                    onClick={() => downloadDoc("sesizare", key, label)}
                    title="Descarcă Sesizare (docx)"
                    data-testid={`download-sesizare-${key}`}
                    className="ml-1 inline-flex items-center gap-1 rounded border border-zinc-700 bg-zinc-800/60 px-2 py-0.5 text-[10px] text-zinc-100 hover:border-orange-500/40"
                  >
                    <Download className="h-3 w-3" /> Sesizare
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadDoc("mesaj", key, label)}
                    title="Descarcă Mesaj eMAG (docx)"
                    data-testid={`download-mesaj-${key}`}
                    className="inline-flex items-center gap-1 rounded border border-zinc-700 bg-zinc-800/60 px-2 py-0.5 text-[10px] text-zinc-100 hover:border-emerald-500/40"
                  >
                    <Download className="h-3 w-3" /> Mesaj docx
                  </button>
                  <button
                    type="button"
                    onClick={() => openMesajPopup(key)}
                    title="Deschide mesajul într-un popup (copy/paste)"
                    data-testid={`popup-mesaj-${key}`}
                    className="inline-flex items-center gap-1 rounded border border-zinc-700 bg-zinc-800/60 px-2 py-0.5 text-[10px] text-zinc-100 hover:border-sky-500/40"
                  >
                    <MessageSquare className="h-3 w-3" /> Mesaj popup
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-900/70 text-[11px] uppercase text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">SKU</th>
                <th className="px-3 py-2 text-left font-medium">Platformă</th>
                <th className="px-3 py-2 text-left font-medium">Nume produs</th>
                <th className="px-3 py-2 text-left font-medium">Seller</th>
                <th className="px-3 py-2 text-left font-medium">Atașament</th>
                <th className="px-3 py-2 text-left font-medium">Mesaj eMAG</th>
                <th className="px-3 py-2 text-left font-medium">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-500">Se încarcă…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-500">
                  Nicio violare activă. 🎉 Toate ofertele active dețin Buy Button-ul.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={`${r.integration_key}-${r.pnk}`} className="border-t border-zinc-900" data-testid={`row-${r.pnk}-${r.integration_key}`}>
                  <td className="px-3 py-3">
                    <div className="font-mono text-xs font-semibold text-orange-200">{r.sku || "—"}</div>
                    <div className="mt-0.5 text-[10px] text-zinc-500">PNK {r.pnk}</div>
                  </td>
                  <td className="px-3 py-3 text-xs text-zinc-300">{r.platform_label}</td>
                  <td className="px-3 py-3">
                    <div className="max-w-md text-[11px] leading-snug text-zinc-200">{r.name || "—"}</div>
                    {r.product_url && (
                      <a href={r.product_url} target="_blank" rel="noreferrer"
                        className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] text-zinc-500 hover:text-zinc-300"
                      >
                        <ExternalLink className="h-2.5 w-2.5" /> deschide pe eMAG
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs">
                    {editRow === `${r.integration_key}-${r.pnk}` ? (
                      <InlineSeller row={r} onCancel={() => setEditRow(null)} onSave={(v) => saveSeller(r, v)} />
                    ) : (
                      <div className="inline-flex items-center gap-1">
                        <span className={r.seller ? "text-zinc-200 font-medium" : "italic text-rose-300/70"}>
                          {r.seller || "necunoscut"}
                        </span>
                        <button
                          type="button"
                          onClick={() => setEditRow(`${r.integration_key}-${r.pnk}`)}
                          data-testid={`edit-seller-${r.pnk}`}
                          className="rounded p-0.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
                          title="Editează seller"
                        >
                          <Edit3 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      onClick={() => downloadDoc("sesizare", r.integration_key, r.platform_label)}
                      data-testid={`row-download-sesizare-${r.pnk}`}
                      className="inline-flex items-center gap-1 rounded-md border border-orange-500/40 bg-orange-500/10 px-2 py-1 text-[11px] text-orange-100 hover:bg-orange-500/20"
                      title="Descarcă Sesizare pentru toată platforma"
                    >
                      <Download className="h-3 w-3" /> Sesizare
                    </button>
                  </td>
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      onClick={() => openMesajPopup(r.integration_key)}
                      data-testid={`row-popup-mesaj-${r.pnk}`}
                      className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[11px] text-sky-100 hover:bg-sky-500/20"
                      title="Deschide mesajul în popup"
                    >
                      <MessageSquare className="h-3 w-3" /> Deschide
                    </button>
                  </td>
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      onClick={() => ignoreProduct(r)}
                      data-testid={`row-ignore-${r.pnk}`}
                      className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-100 hover:bg-rose-500/20"
                      title="Nu mai afișa niciodată acest produs pe această platformă"
                    >
                      <EyeOff className="h-3 w-3" /> Ignoră produs
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {popup && (
        <MesajPopup popup={popup} onClose={() => setPopup(null)} />
      )}
    </div>
  );
}

function InlineSeller({ row, onCancel, onSave }) {
  const [v, setV] = useState(row.seller || "");
  return (
    <span className="inline-flex items-center gap-1">
      <input
        value={v}
        onChange={(e) => setV(e.target.value)}
        autoFocus
        data-testid={`seller-input-${row.pnk}`}
        className="w-32 rounded border border-zinc-700 bg-zinc-900 px-1.5 py-0.5 font-mono text-[11px] text-zinc-100 focus:border-orange-500/50 focus:outline-none"
      />
      <button type="button" onClick={() => onSave(v.trim())}
        data-testid={`seller-save-${row.pnk}`}
        className="rounded p-0.5 text-emerald-400 hover:bg-emerald-500/10">
        <Save className="h-3 w-3" />
      </button>
      <button type="button" onClick={onCancel}
        className="rounded p-0.5 text-zinc-500 hover:bg-zinc-800">
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function MesajPopup({ popup, onClose }) {
  const copy = async (what) => {
    const text = what === "title" ? popup.title
      : what === "body" ? popup.body
      : `${popup.title}\n\n${popup.body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiat în clipboard");
    } catch {
      toast.error("Nu pot copia — permite clipboard-ul");
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="mesaj-popup"
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500">{popup.platform_label}</div>
            <h3 className="text-sm font-semibold text-zinc-100">Mesaj eMAG</h3>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => copy("all")}
              data-testid="popup-copy-all"
              className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-100 hover:bg-emerald-500/20">
              <Copy className="h-3 w-3" /> Copiază tot
            </button>
            <button type="button" onClick={onClose}
              className="rounded p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">Titlu</span>
            <button type="button" onClick={() => copy("title")}
              className="text-[10px] text-emerald-300 hover:text-emerald-100">
              <Copy className="mr-1 inline h-2.5 w-2.5" /> copy titlu
            </button>
          </div>
          <pre data-testid="popup-title-text" className="mb-4 whitespace-pre-wrap break-words rounded-md border border-zinc-800 bg-zinc-900/60 p-2 font-sans text-sm text-orange-100">
            {popup.title}
          </pre>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">Body</span>
            <button type="button" onClick={() => copy("body")}
              className="text-[10px] text-emerald-300 hover:text-emerald-100">
              <Copy className="mr-1 inline h-2.5 w-2.5" /> copy body
            </button>
          </div>
          <pre data-testid="popup-body-text" className="whitespace-pre-wrap break-words rounded-md border border-zinc-800 bg-zinc-900/60 p-3 font-sans text-sm text-zinc-100">
            {popup.body}
          </pre>
        </div>
      </div>
    </div>
  );
}
